import { EntityServiceAdapter, QueryParams, QueryResult } from '../../../types/baseExplorerConfig';
import { affService, AffQueryParams, buildAffQuery } from '../../../services/affService';
import { backendPage, pageHasMore } from '../../../utils/pagination';

export interface AffAircraftEntity {
  _id: string;
  esn: string;
  name: string;
  [key: string]: any;
}

export interface AffTrackEntity {
  _id: string;
  esn: string;
  [key: string]: any;
}

const withSearchAndSort = (params: QueryParams, out: AffQueryParams): AffQueryParams => {
  if (params?.search?.term) out.search = params.search.term;
  if (params?.sort?.[0]) {
    // The grid field is the sortBy: the whitelist is keyed by schema field
    // (AFF_*_SORT_COLUMNS in affSchema), which the router reads too.
    out.sortBy = params.sort[0].field;
    out.sortOrder = params.sort[0].direction;
  }
  return out;
};

/**
 * #866 — a filter the adapter cannot express must fail loudly, not vanish.
 * A dropped filter shows unfiltered rows under an active filter badge, which
 * reads as "no matches were excluded" — worse than an error in the grid.
 */
const unsupported = (grid: string, filter: { field: string; operator: string }): never => {
  throw new Error(`AFF ${grid}: filter ${filter.field} ${filter.operator} is not supported`);
};

/** Fields passed straight through under their own name as a query key. */
const AIRCRAFT_PASSTHROUGH = ['name', 'esn', 'callsign', 'make', 'model', 'owner', 'operationalFunction', 'groupDesignator', 'stale'] as const;
const TRACK_PASSTHROUGH = ['name', 'callsign', 'esn'] as const;

/** Used by fetchData (view) and buildExportParams (export) — one translation, no drift. */
export function translateAircraftParams(params: QueryParams): AffQueryParams {
  const out: AffQueryParams = {};
  withSearchAndSort(params, out);

  for (const filter of params?.filters ?? []) {
    if ((AIRCRAFT_PASSTHROUGH as readonly string[]).includes(filter.field)) {
      out[filter.field as typeof AIRCRAFT_PASSTHROUGH[number]] = filter.value;
      continue;
    }
    unsupported('Aircraft', filter);
  }

  return out;
}

/**
 * positionTimestamp is the observation; createdAt is when we received it. They
 * are different facts and must not collapse into one date range. Only gte/lte
 * exist for either (the grid offers From/To and nothing else).
 */
const TRACK_DATE_KEYS: Record<string, { gte: 'startDate' | 'capturedAfter'; lte: 'endDate' | 'capturedBefore' }> = {
  positionTimestamp: { gte: 'startDate', lte: 'endDate' },
  createdAt: { gte: 'capturedAfter', lte: 'capturedBefore' },
};

export function translateTrackParams(params: QueryParams): AffQueryParams {
  const out: AffQueryParams = {};
  withSearchAndSort(params, out);

  for (const filter of params?.filters ?? []) {
    const dateKeys = TRACK_DATE_KEYS[filter.field];
    if (dateKeys) {
      if (filter.operator !== 'gte' && filter.operator !== 'lte') unsupported('Tracks', filter);
      if (filter.value) out[dateKeys[filter.operator as 'gte' | 'lte']] = String(filter.value);
      continue;
    }

    if ((TRACK_PASSTHROUGH as readonly string[]).includes(filter.field)) {
      out[filter.field as typeof TRACK_PASSTHROUGH[number]] = filter.value;
      continue;
    }

    unsupported('Tracks', filter);
  }

  return out;
}

const aircraftAdapter = (includeSim: boolean): EntityServiceAdapter<AffAircraftEntity> => ({
  async fetchData(params: QueryParams): Promise<QueryResult<AffAircraftEntity>> {
    const response = await affService.fetchAircraft<any>({
      ...backendPage(params),
      ...translateAircraftParams(params),
      includeSim: includeSim || undefined,
    });

    return {
      data: response.data.map((row: any) => ({ ...row, _id: row._id || String(row.esn) })),
      total: response.total,
      page: response.page,
      limit: response.limit,
      hasMore: pageHasMore(response.page, response.limit, response.total),
    };
  },

  buildExportParams: (params: QueryParams) =>
    buildAffQuery(translateAircraftParams(params)),
});

const tracksAdapter = (includeSim: boolean): EntityServiceAdapter<AffTrackEntity> => ({
  async fetchData(params: QueryParams): Promise<QueryResult<AffTrackEntity>> {
    const response = await affService.fetchTracks<any>({
      ...backendPage(params),
      ...translateTrackParams(params),
      includeSim: includeSim || undefined,
    });

    return {
      data: response.data.map((row: any) => ({ ...row, _id: row._id || String(row.id) })),
      total: response.total,
      page: response.page,
      limit: response.limit,
      hasMore: pageHasMore(response.page, response.limit, response.total),
    };
  },

  buildExportParams: (params: QueryParams) =>
    buildAffQuery(translateTrackParams(params)),
});

// Exports never include simulated aircraft: buildExportParams does not pass includeSim.
export const affAircraftAdapter = aircraftAdapter(false);
export const affAircraftAdapterWithSim = aircraftAdapter(true);
export const affTracksAdapter = tracksAdapter(false);
export const affTracksAdapterWithSim = tracksAdapter(true);
